import { useEffect, useState } from "react";
import { AlertCircle, BellOff, Mail, Moon, RefreshCw } from "lucide-react";
import { getTicketEmailPreferences, updateTicketEmailPreference } from "../lib/api";
import type { TicketEmailDeliveryMode, TicketEmailPreference, Viewer } from "../types";
import { PageHeading } from "./PageHeading";

const MODES: Array<{ value: TicketEmailDeliveryMode; label: string; description: string; icon: typeof Mail }> = [
  { value: "immediate", label: "Individual emails", description: "Send an email after every cross-column move.", icon: Mail },
  { value: "daily_digest", label: "Nightly roundup", description: "Combine the day’s moves into one nightly email.", icon: Moon },
  { value: "off", label: "Off", description: "Do not send ticket movement email.", icon: BellOff }
];

export function TicketEmailSettingsPage({ viewer }: { viewer: Viewer }) {
  const [preferences, setPreferences] = useState<TicketEmailPreference[]>([]);
  const [schedule, setSchedule] = useState({ localTime: "11:00 PM", timeZone: "America/Chicago" });
  const [emailDeliveryEnabled, setEmailDeliveryEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingUid, setSavingUid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { const result = await getTicketEmailPreferences(); setPreferences(result.preferences); setSchedule(result.digestSchedule); setEmailDeliveryEnabled(result.emailDeliveryEnabled); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to load ticket email preferences"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const update = async (preference: TicketEmailPreference, deliveryMode: TicketEmailDeliveryMode) => {
    if (!preference.canEdit || preference.deliveryMode === deliveryMode || savingUid) return;
    const previous = preferences;
    setSavingUid(preference.uid); setError(null); setNotice(null);
    setPreferences((current) => current.map((entry) => entry.uid === preference.uid ? { ...entry, deliveryMode } : entry));
    try {
      await updateTicketEmailPreference(preference.uid, deliveryMode);
      setNotice(`Email preference updated for ${preference.email}.`);
    } catch (reason) {
      setPreferences(previous);
      setError(reason instanceof Error ? reason.message : "Email preference could not be updated");
    } finally { setSavingUid(null); }
  };

  return <div className="page-stack ticket-email-settings-page">
    <PageHeading eyebrow="TICKET COMMUNICATIONS" title="Ticket email notifications" description="Choose how each active administrator receives Kanban movement updates."
      actions={<button className="secondary-button" onClick={() => void load()} disabled={loading}><RefreshCw size={16} className={loading ? "spin" : undefined} />Refresh</button>} />
    <div className={`ticket-email-summary${emailDeliveryEnabled ? "" : " ticket-email-paused"}`}><Mail size={20} /><div><strong>{emailDeliveryEnabled ? "Postmark delivery" : "Email delivery paused"}</strong><p>{emailDeliveryEnabled ? "Cross-column moves email every active admin according to the mode below. Reordering inside a column stays silent." : "Preferences can be configured now, but moves will not queue email until Postmark is enabled."}</p></div><span>{emailDeliveryEnabled ? `Digest: ${schedule.localTime} ${schedule.timeZone}` : "Postmark setup pending"}</span></div>
    {notice ? <div className="success-panel">{notice}</div> : null}
    {error ? <div className="error-panel"><AlertCircle size={19} /><div><strong>Email settings unavailable</strong><p>{error}</p></div></div> : null}
    <section className="history-card ticket-email-board" aria-busy={loading}>
      <div className="section-heading-row"><div><p className="section-label">ACTIVE ADMINISTRATORS</p><h2>Delivery modes</h2></div><span className="history-count">{loading ? "Loading…" : `${preferences.length} recipients`}</span></div>
      <div className="ticket-email-list">
        {preferences.map((preference) => <article key={preference.uid}>
          <div className="ticket-email-person"><span className="avatar">{preference.email.charAt(0).toUpperCase()}</span><div><strong>{preference.email}</strong><small>{preference.role === "superAdmin" ? "Super admin" : preference.role ? "Read only" : "Unknown role"}{preference.uid === viewer.uid ? " · You" : ""}</small></div></div>
          <div className="ticket-email-modes">
            {MODES.map(({ value, label, description, icon: Icon }) => <label className={preference.deliveryMode === value ? "ticket-email-mode selected" : "ticket-email-mode"} key={value}>
              <input type="radio" name={`delivery-${preference.uid}`} value={value} checked={preference.deliveryMode === value} disabled={!preference.canEdit || savingUid !== null} onChange={() => void update(preference, value)} />
              <Icon size={17} /><span><strong>{label}</strong><small>{description}</small></span>
            </label>)}
          </div>
          {!preference.canEdit ? <p className="ticket-email-lock">Only a Super admin can change this row.</p> : null}
        </article>)}
        {!loading && preferences.length === 0 ? <div className="ticket-email-empty">No active administrators were found.</div> : null}
      </div>
    </section>
  </div>;
}
